<!--
/*
 * Компонент EmailBindDialog — привязка email через magic link
 */
-->
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { EventLogger } from '@/utils/EventLogger'
import { validateEmail } from '@/utils/validateEmail'
import OtpCodeInput from '@/modules/auth/components/OtpCodeInput.vue'
import { useI18n } from 'vue-i18n'

interface IEmailBindDialogProps {
  modelValue: boolean
}

defineProps<IEmailBindDialogProps>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  emailBound: []
}>()

const userStore = useUserStore()
const authStore = useAuthStore()
const feedbackStore = useFeedbackStore()
const { t } = useI18n()

const email = ref('')
const error = ref('')
const info = ref('')
const isLoading = ref(false)
const showOtp = ref(false)
const otpError = ref('')
const cooldown = ref(0)
const showDiscardConfirm = ref(false)

const hasProgress = computed(() => showOtp.value || email.value.trim().length > 0)

const { pause: pauseCooldown, resume: resumeCooldown } = useIntervalFn(
  () => {
    cooldown.value--
    if (cooldown.value <= 0) pauseCooldown()
  },
  1000,
  { immediate: false },
)

const startCooldown = () => {
  cooldown.value = 60
  resumeCooldown()
}

const handleSendLink = async () => {
  error.value = ''
  info.value = ''

  if (!validateEmail(email.value)) {
    error.value = t('errors.invalidEmail')
    return
  }

  isLoading.value = true
  try {
    const updateResult = await userStore.updateUserEmail(email.value)
    if (!updateResult.ok) {
      error.value = updateResult.error || t('errors.updateEmailFailed')
      return
    }

    info.value = t('auth.codeSentTo')
    showOtp.value = true
    otpError.value = ''
    startCooldown()
  } catch (err: unknown) {
    EventLogger.error('EmailBindDialog', [`Ошибка: ${(err as Error).message}`])
    error.value = t('errors.tryLater')
  } finally {
    isLoading.value = false
  }
}

const handleOtpComplete = async (code: string) => {
  otpError.value = ''
  isLoading.value = true
  try {
    const result = await authStore.validateMagicLink({ email: email.value, token: code })

    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
      resetDialogState()
      emit('update:modelValue', false)
      emit('emailBound')
    } else {
      otpError.value = result.error || t('errors.invalidCode')
    }
  } catch (err: unknown) {
    EventLogger.error('EmailBindDialog.handleOtpComplete', [`Ошибка: ${(err as Error).message}`])
    otpError.value = t('errors.tryLater')
  } finally {
    isLoading.value = false
  }
}

const handleOtpResend = async () => {
  await handleSendLink()
}

const handleBackToEmail = () => {
  showOtp.value = false
  otpError.value = ''
  info.value = ''
}

const resetDialogState = () => {
  email.value = ''
  error.value = ''
  info.value = ''
  otpError.value = ''
  showOtp.value = false
}

const handleVisibilityChange = (value: boolean) => {
  if (value) {
    emit('update:modelValue', true)
    return
  }
  if (hasProgress.value) {
    showDiscardConfirm.value = true
    return
  }
  emit('update:modelValue', false)
}

const confirmDiscard = () => {
  showDiscardConfirm.value = false
  resetDialogState()
  emit('update:modelValue', false)
}

const cancelDiscard = () => {
  showDiscardConfirm.value = false
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="440"
    :scrim="true"
    class="blur-dialog-overlay"
    @update:model-value="handleVisibilityChange"
  >
    <v-card class="pa-6" color="surface" rounded="xl">
      <div class="d-flex align-start ga-2">
        <div class="d-flex flex-column ga-2 flex-grow-1">
          <span class="text-h4">{{ t('emailBind.title') }}</span>
          <span class="text-caption">
            {{ t('emailBind.desc') }}
          </span>
        </div>
        <v-btn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="handleVisibilityChange(false)"
        />
      </div>

      <div class="d-flex flex-column ga-4 mt-4">
        <v-text-field
          v-model="email"
          label="Email"
          placeholder="name@example.com"
          type="email"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          autocomplete="email"
          :readonly="showOtp"
          @keydown.enter="handleSendLink"
        />

        <v-alert
          v-if="info"
          type="info"
          variant="tonal"
          density="compact"
          class="text-caption"
        >
          {{ info }}
        </v-alert>

        <otp-code-input
          v-if="showOtp"
          :loading="isLoading"
          :error="otpError"
          @complete="handleOtpComplete"
          @resend="handleOtpResend"
        />

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="text-caption"
        >
          {{ error }}
        </v-alert>

        <v-btn
          v-if="showOtp"
          variant="text"
          rounded="lg"
          block
          prepend-icon="ri-arrow-left-line"
          @click="handleBackToEmail"
        >
          {{ t('emailBind.backToEmail') }}
        </v-btn>

        <v-btn
          v-if="!showOtp"
          color="primary"
          block
          rounded="lg"
          class="font-weight-bold"
          :loading="isLoading"
          :disabled="cooldown > 0"
          @click="handleSendLink"
        >
          <template v-if="cooldown > 0">
            {{ t('auth.resendCodeCount', { seconds: cooldown }) }}
          </template>
          <template v-else>
            {{ t('emailBind.sendLink') }}
          </template>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showDiscardConfirm" max-width="400" persistent class="blur-dialog-overlay">
    <v-card class="pa-5" color="surface" rounded="xl">
      <div class="d-flex flex-column ga-4">
        <div class="d-flex flex-column ga-2">
          <span class="text-h6">{{ t('emailBind.discardTitle') }}</span>
          <span class="text-caption text-medium-emphasis">
            {{ t('emailBind.discardDesc') }}
          </span>
        </div>
        <div class="d-flex flex-column ga-2">
          <v-btn
            color="primary"
            rounded="lg"
            block
            @click="cancelDiscard"
          >
            {{ t('common.cancel') }}
          </v-btn>
          <v-btn
            variant="text"
            color="error"
            rounded="lg"
            block
            @click="confirmDiscard"
          >
            {{ t('common.close') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>
